import { formatDateOnly } from "@/lib/date";

// Shared lettering-policy helpers: path labels, rule summaries and progress snippets.
const yrs = (n) => `${n} school yr${n === 1 ? "" : "s"}`;

export const PATH_ORDER = ["points", "performance", "active_years", "subgroup", "coach"];

export const PATH_LABELS = {
  points: "Point threshold",
  performance: "Performance count",
  active_years: "Active years",
  subgroup: "Subgroup membership",
  coach: "Coach discretion",
};

export const PATH_HOW = { points: "points", performance: "performances", active_years: "active years", subgroup: "subgroup", coach: "coach award" };

export const fmtDay = (d) => (d ? formatDateOnly(d) : "");

export const subsequentPaths = (p) => (p?.subsequent_mirror ? p.first : p?.subsequent) || {};

export function pathRule(path, c) {
  switch (path) {
    case "points": return `${c.points} pts within ${yrs(c.years)}`;
    case "performance": return `${c.count} approved submission${c.count === 1 ? "" : "s"} within ${yrs(c.years)}`;
    case "active_years": return `${yrs(c.years)} in a row`;
    case "subgroup": return `Member of ${c.subgroup_name || "a group"}${c.completion_date ? ` through ${fmtDay(c.completion_date)}` : ""}`;
    default: return "Coach award";
  }
}

export function pathSummary(paths) {
  const on = PATH_ORDER.filter((k) => paths?.[k]?.enabled).map((k) => pathRule(k, paths[k]));
  return on.length ? on.join(" or ") : "No paths turned on";
}

// Short "what's left" snippets per active path, for tables.
export function progressParts(prog) {
  const out = [];
  if (!prog) return out;
  const { points: p, performance: f, active_years: a, subgroup: g } = prog;
  if (p) out.push(`${Math.max(0, p.need - p.have)} pts to go`);
  if (f) out.push(`${Math.max(0, f.need - f.have)} submission${f.need - f.have === 1 ? "" : "s"} to go`);
  if (a) out.push(`yr ${Math.min(a.have, a.need)}/${a.need} in a row`);
  if (g) out.push(!g.in_group ? `not in ${g.name || "group"}` : !g.joined_ok ? `joined ${g.name || "group"} late` : `in ${g.name || "group"}${g.completion_date ? ` until ${fmtDay(g.completion_date)}` : ""}`);
  return out;
}

// Progress bar milestone for the first active computed path (coach-only → a gentle note).
export function pathMilestone(prog, goal) {
  if (!prog) return null;
  const { points: p, performance: f, active_years: a, subgroup: g } = prog;
  if (p) return { have: Math.min(p.have, p.need), need: p.need, label: `${p.have} / ${p.need} pts · ${Math.max(0, p.need - p.have)} more to ${goal}` };
  if (f) return { have: Math.min(f.have, f.need), need: f.need, label: `${f.have} / ${f.need} approved submissions · ${Math.max(0, f.need - f.have)} more to ${goal}` };
  if (a) {
    const label = a.missed_this_year ? "This school year is marked missed — your active-year streak restarts next year"
      : `Year ${Math.min(a.have, a.need)} of ${a.need} in a row toward ${goal}${a.good_standing ? "" : " · paused until good standing is restored"}`;
    return { have: Math.min(a.have, a.need), need: a.need, label };
  }
  if (g) {
    const nm = g.name || "your group";
    const label = !g.in_group ? `Join ${nm} to earn ${goal}`
      : !g.joined_ok ? `Joined ${nm} after ${fmtDay(g.completion_date)} — doesn't count this season`
      : `Stay in ${nm} through ${fmtDay(g.completion_date)} to earn ${goal}`;
    return { have: g.in_group && g.joined_ok ? 1 : 0, need: 2, label };
  }
  if (prog.coach?.enabled) return { have: 0, need: 1, label: `Your coach can award ${goal}` };
  return null;
}
